import { useEffect, useRef, useState } from 'react';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import {
  CAUSE_LABELS,
  REJECT_CAUSES,
  formatAge,
  liveHours,
  type RejectCause,
  type ReviewRowData,
} from './reviewTypes';

/**
 * Rejecting a translation readers can already see.
 *
 * The dialog's job is to state the **reader-facing consequence before** the
 * reviewer confirms it. Rejecting withdraws the translation — the reviewer
 * is saying it should not be read, so it stops being read — and that is a
 * bigger thing than it looks from an admin screen, because somebody may be
 * reading it right now.
 *
 * Nothing is deleted. The text, the causes and the note stay, so the
 * translator can see what was wrong with it.
 */
export function RejectDialog({
  row,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  row: ReviewRowData;
  pending: boolean;
  error: string | null;
  onConfirm: (causes: RejectCause[], note: string, keepReadable: boolean) => void;
  onCancel: () => void;
}) {
  const [causes, setCauses] = useState<RejectCause[]>([]);
  const [note, setNote] = useState('');
  const [keepReadable, setKeepReadable] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);

  // Focus moves into the dialog, and Escape leaves it — the two things a
  // keyboard user needs from anything modal.
  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  const hours = liveHours(row);
  const ready = causes.length > 0 && note.trim() !== '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-overlay p-0 sm:items-center sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reject-title"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            onCancel();
          }
        }}
        className="flex max-h-[92dvh] w-full max-w-[560px] flex-col gap-4 overflow-auto rounded-t-xl border border-border bg-card p-5 sm:rounded-xl"
      >
        <div>
          <h2 id="reject-title" className="text-base font-semibold">
            Reject the {row.languageName} translation
          </h2>
          {row.titleAr ? (
            <p lang="ar" dir="rtl" className="script-ar mt-1.5 text-[15px] font-normal">
              {row.titleAr}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-muted-foreground">
            Friday {row.friday}
            {hours !== null ? ` · readers have seen it for ${formatAge(hours)}` : null}
          </p>
        </div>

        {/* The consequence, before the decision — not after it. */}
        <div
          className={cn(
            'rounded-lg border p-3 text-[13px] leading-[18px]',
            keepReadable ? 'border-border bg-muted' : 'border-destructive',
          )}
        >
          {keepReadable ? (
            <>
              <b>Readers keep seeing it.</b> It stays published and flagged for the
              translator. Choose this only when the problem is smaller than the
              harm of taking the language away.
            </>
          ) : (
            <>
              <b>Readers stop seeing it immediately.</b> The {row.languageName}{' '}
              translation is withdrawn; the Arabic and any other language stay.
              Nothing is deleted — it goes back to the translator with your reason.
            </>
          )}
        </div>

        <fieldset>
          <legend className="text-[13px] font-medium">
            What is wrong? <span className="text-muted-foreground">(at least one)</span>
          </legend>
          <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
            {REJECT_CAUSES.map((cause, index) => (
              <label key={cause} className="flex items-center gap-2 text-sm">
                <input
                  ref={index === 0 ? firstRef : undefined}
                  type="checkbox"
                  checked={causes.includes(cause)}
                  onChange={(event) =>
                    setCauses((current) =>
                      event.target.checked
                        ? [...current, cause]
                        : current.filter((c) => c !== cause),
                    )
                  }
                />
                {CAUSE_LABELS[cause]}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">
            Which passages, and what is wrong with them?
          </span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={4}
            maxLength={4000}
            placeholder="Passage 4 drops the second half of the sentence."
            className="w-full resize-y rounded-md border border-border bg-background p-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
          {/* Required, and the reason is said rather than implied. */}
          <span className="text-xs text-muted-foreground">
            Required — it is what the translator has to act on.
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={keepReadable}
            onChange={(event) => setKeepReadable(event.target.checked)}
            className="mt-0.5"
          />
          <span>
            Leave it readable while flagged
            <span className="block text-xs text-muted-foreground">
              The exception, not the default.
            </span>
          </span>
        </label>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            size="lg"
            variant="destructive"
            disabled={!ready || pending}
            onClick={() => onConfirm(causes, note.trim(), keepReadable)}
          >
            {pending
              ? 'Rejecting…'
              : keepReadable
                ? 'Reject, keep readable'
                : 'Reject and withdraw'}
          </Button>
        </div>
      </div>
    </div>
  );
}
